import { setBelongingInformation } from "@vrf/fr-vrf-auth-core";
import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating/lib/core/cell";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfMultipleLineInputField,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import {
  DeploymentRequestDetailPageInitialState,
  DeploymentRequestDetailPageState,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages";
import {
  useGetEdhNotificationTypeList,
  useGetGeneralCodes,
} from "../../services/data-list";
import {
  useGetDeploymentRequest,
  useUpdateInterfaceDeploymentRequestStatus,
} from "../../services/deployment-request";
import {
  GetIFDefinitionsRequest,
  useGetIFDefinitions,
} from "../../services/if-definitions";
import { useGetActionItems } from "../../services/screen-authorization/ScreenAuthorizationHook";
import {
  INIT_ERROR_RESULT_DATA,
  mappingErrorResponseToSpreadsheet,
} from "../../util";
import {
  extractDate,
  extractHourAndMinute,
  removeMilliseconds,
} from "../../util/date-util";
import { ENV } from "@vrf/fr-vrf-core";
import {
  DeploymentDivisionCode,
  DeploymentRequestProcessingDivisionType,
  DeploymentRequestResultDivisionCode,
  DeploymentRequestScreenProcessingDivisionType,
  DeploymentRequestType,
  DsplyType,
  EnvTypeCd,
  InDefCommonFlagType,
  InterfaceRequirementType,
  InterruptType,
  ReferenceModeType,
  ReviewActionType,
  RoleCvrgType,
  SrcDstTypeCd,
  TcktStsTypeIfPrdDplymnt,
  TcktStsTypeIfStgDplymnt,
} from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import {
  DeploymentRequestInfomationInputDialogContent,
  DeploymentRequestInfomationInputDialogInitialState,
  DeploymentRequestInfomationInputDialogState,
  UpdateIfList,
  UpdateIfListMap,
  useDeploymentRequestInfomationInputDialogState,
} from "../dialog/deployment-request-infomation-input-dialog";
import { DPLY_IMPL_TYPE } from "../dialog/deployment-request-infomation-input-dialog/DeploymentRequestInfomationInputDialogConstant";
import {
  EdhNotificationType,
  INIT_FILE_LIST,
  Information,
  Interface,
  InterfaceSpreadsheetRow,
  SpreadsheetState,
  shouldSetInterruptType,
  shouldShowInterruptType,
} from "../interface-definitions";
import {
  CD_TYPES,
  PAGE_MODE as IF_LIST_PAGE_MODE,
} from "../interface-definitions/InterfaceListPageConstant";
import { usePageNavigation } from "../use-page-navigation";
import {
  INTERRUPT_TYPE_PENDING,
  TEMPLATE_FILE_URL,
} from "./DeploymentRequestDetailConstant";
import _ from "lodash";

export const DeploymentRequestDetailPageContent = () => {
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [authdActionItems, sendGetActionItems] = useGetActionItems();

  const { errorMessages, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { isModalPage, initUserError } = usePageNavigation();
  const navigate = useNavigate();

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };
  const { messageForScreen } = useMessage();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    dplymntReqSeq: queryParameter.get("dplymntReqSeq") ?? "",
    screenMode: queryParameter.get("screenMode") ?? "",
  };

  const pageMode = useMemo(() => {
    return params.screenMode;
  }, [params.screenMode]);

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    const ifList: InterfaceSpreadsheetRow[] = pageState.ifList.map((e) => ({
      ...e,
      srcInterruptType:
        e.src.interfaceDefinition.dipByRegion !==
          e.dst.interfaceDefinition.dipByRegion && !!e.srcDipSelect
          ? e.src?.interfaceDefinition.outbound?.interruptType
          : undefined,
      dstInterruptType:
        e.src.interfaceDefinition.dipByRegion ===
          e.dst.interfaceDefinition.dipByRegion || !!e.dstDipSelect
          ? e.dst?.interfaceDefinition.outbound?.interruptType
          : undefined,
      ...INIT_ERROR_RESULT_DATA,
      shouldShowInterruptTypeFlag: shouldShowInterruptType(e),

      srcMetaOnlyFlag:
        e.src.interfaceDefinition.metaOnlyFlag === InDefCommonFlagType.YES,
      dstMetaOnlyFlag:
        e.dst.interfaceDefinition.metaOnlyFlag === InDefCommonFlagType.YES,
    }));
    return {
      ifList,
      interfaceListGeneralCodeState: pageState.generalCode,
      edhNotificationType: pageState.edhNotificationType,
      mode:
        pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.PRD
          ? IF_LIST_PAGE_MODE.PRD_DPLYMNT_MODE
          : IF_LIST_PAGE_MODE.STG_DPLYMNT_MODE,
      dplymntType: pageState.deploymentRequestDetail.dplymntType,
      showPending:
        pageState.deploymentRequestDetail.dplymntResultKbn ===
        DeploymentRequestResultDivisionCode.DEPLOYED,
      deploymentRequestScreenProcessingDivisionType: params.screenMode,
      isDplymntDetailScreen: true,
    };
  }, [
    pageState.ifList,
    pageState.generalCode,
    pageState.edhNotificationType,
    pageState.deploymentRequestDetail,
    params.screenMode,
  ]);

  const [getDeploymentRequestResponse, sendGetDeploymentRequestRequest] =
    useGetDeploymentRequest();
  const [getIFDefinitionsResponse, sendGetIFDefinitionsRequest] =
    useGetIFDefinitions();
  const [getGeneralCodesResponse, sendGeneralCodesRequest] =
    useGetGeneralCodes();
  const [
    getEdhNotificationTypeListResponse,
    sendEdhNotificationTypeListRequest,
  ] = useGetEdhNotificationTypeList();

  const [
    updateInterfaceDeploymentRequestStatusResponse,
    sendUpdateInterfaceDeploymentRequestStatusRequest,
  ] = useUpdateInterfaceDeploymentRequestStatus();

  const [
    deploymentRequestInfomationInputDialogState,
    setDeploymentRequestInfomationInputDialogState,
  ] = useDeploymentRequestInfomationInputDialogState();

  const templateFileUrl = useMemo(() => {
    let templateUrl = TEMPLATE_FILE_URL.REFERENCE;
    switch (pageMode) {
      case DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE:
        templateUrl = TEMPLATE_FILE_URL.REFERENCE;
        break;

      case DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE:
        templateUrl = TEMPLATE_FILE_URL.DEPLOYMENT_SETUP_PENDING;
        break;

      case DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE:
        templateUrl = TEMPLATE_FILE_URL.DEPLOYMENT_SETUP_PENDING;
        break;

      case DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE:
        templateUrl = TEMPLATE_FILE_URL.DEPLOYMENT_SETUP_PENDING;
        break;

      case DeploymentRequestScreenProcessingDivisionType.RETRY_MODE:
        templateUrl = TEMPLATE_FILE_URL.DEPLOYMENT_SETUP_PENDING;
        break;

      case DeploymentRequestScreenProcessingDivisionType.REVERT_MODE:
        templateUrl = TEMPLATE_FILE_URL.REFERENCE;
        break;
    }
    return templateUrl;
  }, [pageMode]);

  const onInitialDisplayed = () => {
    initUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: `Interface Deployment Request Detail (Deployment Request SEQ: ${params.dplymntReqSeq})`,
    }));

    sendGetDeploymentRequestRequest({
      dplymntReqSeq: params.dplymntReqSeq,
    });
    sendGeneralCodesRequest({ cdTypes: CD_TYPES });
    sendEdhNotificationTypeListRequest({});
  };

  const onGetDeploymentRequestResponded = () => {
    if (!getDeploymentRequestResponse) return;

    const pjtId =
      getDeploymentRequestResponse.deploymentRequestDetail.jediPjtId || "";
    setAppState((prevState) => ({ ...prevState, pjtId: pjtId }));

    sendGetIFDefinitionsRequest({
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
      project: {
        pjtIds: [pjtId],
        dplymntReqSeqNum: +params.dplymntReqSeq,
      },
    });

    if (!!pjtId) {
      setBelongingInformation({
        belongTypeCode: RoleCvrgType.PROJECT,
        belongCode: pjtId,
      });
      sendGetActionItems({
        blngTypeCd: RoleCvrgType.PROJECT,
        blngCd: pjtId,
      });
    }

    setPageState((prevState) => ({
      ...prevState,
      deploymentRequestDetail:
        getDeploymentRequestResponse.deploymentRequestDetail ??
        DeploymentRequestDetailPageInitialState.deploymentRequestDetail,
      tckIdList: getDeploymentRequestResponse?.tckIdList ?? [],
    }));

    const iFDefinitionsRequest: GetIFDefinitionsRequest = {
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
      project: {
        pjtIds: [
          getDeploymentRequestResponse.deploymentRequestDetail.jediPjtId,
        ],
        dplymntReqSeqNum:
          +getDeploymentRequestResponse.deploymentRequestDetail.dplymntReqSeq,
      },
    };
    sendGetIFDefinitionsRequest(iFDefinitionsRequest);
  };

  const onGetIFDefinitionsResponse = () => {
    const ifList = getIFDefinitionsResponse?.ifList ?? [];
    if (ifList.length === 0) {
      return;
    }
    if (!getDeploymentRequestResponse) return;
    const interfaceList: Interface[] = ifList.map((e, idx) => {
      const information: Information = {
        ...e.information,
        val: "",
        col: [],
      };
      const tckIdList: string[] = getDeploymentRequestResponse?.tckIdList.map(
        (e) => {
          return e.tckId;
        },
      );
      const srcDipSelect =
        pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.PRD
          ? tckIdList.includes(e.projectTcktInfo.srcPrdDplymntTcktId)
          : tckIdList.includes(e.projectTcktInfo.srcStgDplymntTcktId);
      const dstDipSelect =
        pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.PRD
          ? tckIdList.includes(e.projectTcktInfo.dstPrdDplymntTcktId)
          : tckIdList.includes(e.projectTcktInfo.dstStgDplymntTcktId);

      const res: InterfaceSpreadsheetRow = {
        ...INIT_ERROR_RESULT_DATA,
        idx,
        isAddRow: false,
        existSrcDsgnCmnt: !!e.projectTcktInfo?.srcDsgnCmnt,
        existDstDsgnCmnt: !!e.projectTcktInfo?.dstDsgnCmnt,
        srcDipSelect: srcDipSelect,
        dstDipSelect: dstDipSelect,
        srcInterruptType:
          srcDipSelect === true &&
          e.src.interfaceDefinition.dipByRegion !==
            e.dst.interfaceDefinition.dipByRegion
            ? e?.src.interfaceDefinition.outbound?.interruptType
            : undefined,
        dstInterruptType:
          dstDipSelect === true
            ? e?.dst.interfaceDefinition.outbound?.interruptType
            : undefined,
        srcFileList: [...INIT_FILE_LIST],
        dstFileList: [...INIT_FILE_LIST],
        srcInboundOutputFileList: [...INIT_FILE_LIST],
        updateDateTime: e.updateDateTime,
        project: e.project,
        projectTcktInfo: e.projectTcktInfo,
        src: e.src,
        dst: e.dst,
        information,
      };
      return res;
    });

    setPageState((prevState) => ({
      ...prevState,
      ifList: interfaceList,
    }));
  };

  const onGetGeneralCodesResponse = () => {
    let commonConditionFlagType = [] as Option[];
    let securityContentsType = [] as Option[];
    let sendDatalakeType = [] as Option[];
    let fileExtensionType = [] as Option[];
    let referenceModeType = [] as Option[];
    let godReferenceModeType = [] as Option[];
    let reviewActionType = [] as Option[];
    let reviewingActionType = [] as Option[];
    let designCompletedActionType = [] as Option[];
    let interfaceRequirementType = [] as Option[];
    let edhNotificationType = [] as Option[];
    let edhPrtclType = [] as Option[];
    let ptnrType = [] as Option[];
    let chnTypeType = [] as Option[];
    let fileTypeType = [] as Option[];
    let ifCycleType = [] as Option[];
    let snapshotIncrementalDeltaType = [] as Option[];
    let dataScopeType = [] as Option[];
    let encriptionType = [] as Option[];
    let securityInformationLevelType = [] as Option[];
    let tcktStsTypePrjPrgrs = [] as Option[];
    let retentionPeriodDays = [] as Option[];
    let interruptType = [] as Option[];
    let interruptTypeForDstUpdate = [] as Option[];
    let interruptTypeForSrcOnlyUpdate = [] as Option[];
    let interruptTypeForSrcDstUpdate = [] as Option[];
    const GeneralCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];
    if (GeneralCode.length === 0) {
      return;
    }
    GeneralCode.map((e) => {
      switch (e.cdType) {
        case "in_def_common_flag_type":
          commonConditionFlagType = e.cdList;
          break;
        case "security_contents_type":
          securityContentsType = e.cdList;
          break;
        case "send_datalake_type":
          sendDatalakeType = e.cdList;
          break;
        case "file_extension_type":
          fileExtensionType = e.cdList;
          break;
        case "reference_mode_type":
          referenceModeType = e.cdList;
          godReferenceModeType = e.cdList.filter((e) => {
            if (
              e.value === ReferenceModeType.ENVIRONMENT_PRD ||
              e.value === ReferenceModeType.ENVIRONMENT_STG
            ) {
              return e;
            }
          });
          break;
        case "review_action_type":
          reviewActionType = e.cdList;
          reviewActionType.forEach((e) => {
            if (
              e.value === ReviewActionType.APPROVE ||
              e.value === ReviewActionType.REJECT
            )
              reviewingActionType.push(e);
            if (e.value === ReviewActionType.DISMISS_APPROVAL)
              designCompletedActionType.push(e);
          });
          break;
        case "interface_requirement_type":
          interfaceRequirementType = e.cdList;
          break;
        case "edh_notification_type":
          edhNotificationType = e.cdList;
          break;
        case "edh_prtcl_type":
          edhPrtclType = e.cdList;
          break;
        case "ptnr_type":
          ptnrType = e.cdList;
          break;
        case "chn_type_type":
          chnTypeType = e.cdList;
          break;
        case "file_type_type":
          fileTypeType = e.cdList;
          break;
        case "if_cycle_type":
          ifCycleType = e.cdList;
          break;
        case "snapshot_incremental_delta_type":
          snapshotIncrementalDeltaType = e.cdList;
          break;
        case "data_scope_type":
          dataScopeType = e.cdList;
          break;
        case "encription_type":
          encriptionType = e.cdList;
          break;
        case "security_information_level_type":
          securityInformationLevelType = e.cdList;
          break;
        case "tckt_sts_type_prj_prgrs":
          tcktStsTypePrjPrgrs = e.cdList;
          break;
        case "retention_period_days_type":
          retentionPeriodDays = e.cdList;
          break;
        case "interrupt_type":
          interruptTypeForDstUpdate = e.cdList.filter(
            (e) => e.value != InterruptType.CANCEL,
          );
          interruptType = interruptTypeForDstUpdate.filter(
            (e) => e.value != InterruptType.NOCHANGE,
          );
          interruptTypeForSrcOnlyUpdate = interruptTypeForDstUpdate.filter(
            (e) => e.value != InterruptType.NONE,
          );
          interruptTypeForSrcDstUpdate = interruptTypeForDstUpdate.filter(
            (e) => e.value != InterruptType.PENDING,
          );
      }
    });
    setPageState((prevState) => ({
      ...prevState,
      generalCode: {
        ...prevState.generalCode,
        commonConditionFlagType: commonConditionFlagType,
        securityContentsType: securityContentsType,
        sendDatalakeType: sendDatalakeType,
        fileExtensionType: fileExtensionType,
        referenceModeType: referenceModeType,
        godReferenceModeType: godReferenceModeType,
        reviewActionType: reviewActionType,
        reviewingActionType: reviewingActionType,
        designCompletedActionType: designCompletedActionType,
        interfaceRequirementType: interfaceRequirementType,
        edhNotificationType: edhNotificationType,
        edhPrtclType: edhPrtclType,
        ptnrType: ptnrType,
        chnTypeType: chnTypeType,
        fileTypeType: fileTypeType,
        ifCycleType: ifCycleType,
        snapshotIncrementalDeltaType: snapshotIncrementalDeltaType,
        dataScopeType: dataScopeType,
        encriptionType: encriptionType,
        securityInformationLevelType: securityInformationLevelType,
        tcktStsTypePrjPrgrs: tcktStsTypePrjPrgrs,
        retentionPeriodDays: retentionPeriodDays,
        interruptType: interruptType,
        interruptTypeForDstUpdate: interruptTypeForDstUpdate,
        interruptTypeForSrcOnlyUpdate: interruptTypeForSrcOnlyUpdate,
        interruptTypeForSrcDstUpdate: interruptTypeForSrcDstUpdate,
      },
    }));
  };

  const onGetEdhNotificationTypeList = () => {
    if (!getEdhNotificationTypeListResponse) {
      return;
    }
    let edhNotificationType: EdhNotificationType = {};
    getEdhNotificationTypeListResponse?.edhNotificationTypeList.forEach((e) => {
      const key = e.inOutTypeCd + "/" + e.dipByRgn;
      if (!edhNotificationType.hasOwnProperty(key)) {
        edhNotificationType[key] = {
          edhNotificationTypeList: [],
        };
      }
      const edhNotificationTypeList =
        edhNotificationType[key].edhNotificationTypeList;
      edhNotificationTypeList.push({ label: e.label, value: e.value });
      edhNotificationType[key] = {
        edhNotificationTypeList: edhNotificationTypeList,
      };
    });

    setPageState((prevState) => ({
      ...prevState,
      edhNotificationType: edhNotificationType,
    }));
  };

  const onGetAuthdActionItemsResponse = () => {
    if (!authdActionItems) return;
    setPageState((prevState) => ({
      ...prevState,
      authorized: true,
    }));
  };

  const onUpdateInterfaceDeploymentRequestStatusResponded = () => {
    if (!spreadsheetState) return;
    if (updateInterfaceDeploymentRequestStatusResponse == undefined) return;

    if (Array.isArray(updateInterfaceDeploymentRequestStatusResponse)) {
      const currentSpreadsheetRecordData: InterfaceSpreadsheetRow[] =
        spreadsheetState ? [...spreadsheetState.ifList] : [];
      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        updateInterfaceDeploymentRequestStatusResponse,
        currentSpreadsheetRecordData,
        pageState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        ifList: newSpreadsheetRecordData,
      });
    } else {
      if (
        params.screenMode ===
        DeploymentRequestScreenProcessingDivisionType.REVERT_MODE
      ) {
        const newParam = {
          dplymntEnv: pageState.deploymentRequestDetail.dplymntEnv,
        };
        // Display success toast message at transition destination
        setAppState((prevState) => ({
          ...prevState,
          isShowSuccessToast: true,
        }));
        transition(`/interface-deployment-request-list?${stringify(newParam)}`);
      } else {
        const newParam = {
          dplymntReqSeq: params.dplymntReqSeq,
          screenMode: DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE,
        };
        // Display success toast message at transition destination
        setAppState((prevState) => ({
          ...prevState,
          isShowSuccessToast: true,
        }));
        transition(`/deployment-request-detail?${stringify(newParam)}`);
        sendGetDeploymentRequestRequest({
          dplymntReqSeq: params.dplymntReqSeq,
        });
      }
    }
  };

  useEffect(onInitialDisplayed, [params.dplymntReqSeq, params.screenMode]);
  useEffect(onGetDeploymentRequestResponded, [getDeploymentRequestResponse]);
  useEffect(onGetIFDefinitionsResponse, [getIFDefinitionsResponse]);
  useEffect(onGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(onGetEdhNotificationTypeList, [getEdhNotificationTypeListResponse]);
  useEffect(onGetAuthdActionItemsResponse, [authdActionItems]);
  useEffect(onUpdateInterfaceDeploymentRequestStatusResponded, [
    updateInterfaceDeploymentRequestStatusResponse,
  ]);

  /**
   * DeploymentRequestInfo
   */
  const onChangeValueHandle = (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;
    const newDeploymentRequestDetail = pageState.deploymentRequestDetail;
    newDeploymentRequestDetail.rvwCmnt = value;
    setPageState((prevState) => ({
      ...prevState,
      deploymentRequestDetail: newDeploymentRequestDetail,
      formValidation: {
        rvwCmnt: isValid,
      },
    }));
  };

  const onCellEdited = (event: CustomEvent) => {
    const rowIndex = event.detail.cell.data.index as number;
    const inputValue: string = event.detail.cell.value;
    const eventName: string = event.detail.name;

    if (eventName === "srcInterruptType") {
      const spreadsheetData = _.cloneDeep(spreadsheetState);
      if (!spreadsheetData) return;
      spreadsheetData.ifList[rowIndex]["srcInterruptType"] = inputValue;
      setSpreadsheetState(spreadsheetData);
    }
    if (eventName === "dstInterruptType") {
      const spreadsheetData = _.cloneDeep(spreadsheetState);
      if (!spreadsheetData) return;
      spreadsheetData.ifList[rowIndex]["dstInterruptType"] = inputValue;
      setSpreadsheetState(spreadsheetData);
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;

    const eventName: string = event.detail.name;
    const value: string = event.detail.cell.value;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const newSpreadsheetIfList = _.cloneDeep(spreadsheetState);
      if (eventName === "srcImnterruptType") {
        newSpreadsheetIfList.ifList[item.data.index]["srcInterruptType"] =
          value;
        setSpreadsheetState(newSpreadsheetIfList);
      } else if (eventName === "dstInterruptType") {
        newSpreadsheetIfList.ifList[item.data.index]["dstInterruptType"] =
          value;
        setSpreadsheetState(newSpreadsheetIfList);
      }
    });
  };

  /**
   * Footer
   */
  const onClickDiscardButton = () => {
    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
      message: messageForScreen(
        ApplicationMessageScreen.APP_SCN_00159,
        "discard",
      ),
      onOkButtonClicked: () => {
        resetUserError();
        sendUpdateInterfaceDeploymentRequestStatusRequest({
          processTyp: DeploymentRequestProcessingDivisionType.DISCARD,
          dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
          dplymntReqSeq: pageState.deploymentRequestDetail.dplymntReqSeq,
          tcktStsIdFrom: pageState.deploymentRequestDetail.dplymntSts,
          updateDtimeTDplymntReqMng:
            pageState.deploymentRequestDetail.updateDtimeTDplymntReqMng,
        });
      },
    });
  };

  const onClickChangeInterruptTypeButton = () => {
    if (!spreadsheetState) return;

    const ifList: UpdateIfList[] = spreadsheetState.ifList.flatMap((e) => {
      const ifInfoData = {
        interfaceRequirementId: e.project.ifReqId,
        updateDTimeTIfReq: e.updateDateTime,
      };
      const isInternalDIP =
        e.src.interfaceDefinition.dipByRegion ===
        e.dst.interfaceDefinition.dipByRegion;

      let resultIfList: UpdateIfList[] = [];

      if (
        pageState.deploymentRequestDetail.dplymntType ===
        DeploymentDivisionCode.NORMAL
      ) {
        // When deployment type is NORMAL, update pendingFlg.
        if (isInternalDIP) {
          resultIfList.push({
            ...ifInfoData,
            srcDstType: SrcDstTypeCd.DIP_INTERNAL_DESTINATION,
            tcktId:
              pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.STG
                ? e.projectTcktInfo.dstStgDplymntTcktId
                : e.projectTcktInfo.dstPrdDplymntTcktId,
            interruptType: shouldSetInterruptType(
              e,
              SrcDstTypeCd.DIP_INTERNAL_DESTINATION,
              false,
            )
              ? e.dstInterruptType
              : undefined,
          });
          return resultIfList;
        }

        if (pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.STG) {
          if (!!e.srcDipSelect && !!e.dstDipSelect) {
            resultIfList.push({
              ...ifInfoData,
              srcDstType: SrcDstTypeCd.INTERDOMAIN_SOURCE,
              tcktId: e.projectTcktInfo.srcStgDplymntTcktId,
              interruptType: shouldSetInterruptType(
                e,
                SrcDstTypeCd.INTERDOMAIN_SOURCE,
                !!e.srcDipSelect && !!!e.dstDipSelect,
              )
                ? e.srcInterruptType
                : undefined,
            });
          }
          if (!!e.srcDipSelect && !e.dstDipSelect) {
            resultIfList.push({
              ...ifInfoData,
              srcDstType: SrcDstTypeCd.INTERDOMAIN_SOURCE,
              tcktId: e.projectTcktInfo.srcStgDplymntTcktId,
              interruptType: shouldSetInterruptType(
                e,
                SrcDstTypeCd.INTERDOMAIN_SOURCE,
                !!e.srcDipSelect && !!!e.dstDipSelect,
              )
                ? e.srcInterruptType
                : undefined,
            });
          }
          if (!!e.dstDipSelect) {
            resultIfList.push({
              ...ifInfoData,
              srcDstType: SrcDstTypeCd.INTERDOMAIN_DESTINATION,
              tcktId: e.projectTcktInfo.dstStgDplymntTcktId,
              interruptType: shouldSetInterruptType(
                e,
                SrcDstTypeCd.DIP_INTERNAL_DESTINATION,
                !!e.srcDipSelect && !!!e.dstDipSelect,
              )
                ? e.dstInterruptType
                : undefined,
            });
          }
        } else if (
          pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.PRD
        ) {
          if (!!e.srcDipSelect) {
            resultIfList.push(
              {
                ...ifInfoData,
                srcDstType: SrcDstTypeCd.INTERDOMAIN_SOURCE,
                tcktId: e.projectTcktInfo.srcPrdDplymntTcktId,
                interruptType: shouldSetInterruptType(
                  e,
                  SrcDstTypeCd.INTERDOMAIN_SOURCE,
                  false,
                )
                  ? e.srcInterruptType
                  : undefined,
              },
              {
                ...ifInfoData,
                srcDstType: SrcDstTypeCd.INTERDOMAIN_DESTINATION,
                tcktId: e.projectTcktInfo.dstPrdDplymntTcktId,
                interruptType: shouldSetInterruptType(
                  e,
                  SrcDstTypeCd.DIP_INTERNAL_DESTINATION,
                  false,
                )
                  ? e.dstInterruptType
                  : undefined,
              },
            );
          }
        }
      } else {
        // When deployment type is other than NORMAL, do not update pendingFlg.
        if (isInternalDIP) {
          resultIfList.push({
            ...ifInfoData,
            srcDstType: SrcDstTypeCd.DIP_INTERNAL_DESTINATION,
            tcktId:
              pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.STG
                ? e.projectTcktInfo.dstStgDplymntTcktId
                : e.projectTcktInfo.dstPrdDplymntTcktId,
          });
          return resultIfList;
        } else {
        }

        if (pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.STG) {
          if (!!e.srcDipSelect) {
            resultIfList.push({
              ...ifInfoData,
              srcDstType: SrcDstTypeCd.INTERDOMAIN_SOURCE,
              tcktId: e.projectTcktInfo.srcStgDplymntTcktId,
            });
          }
          if (!!e.dstDipSelect) {
            resultIfList.push({
              ...ifInfoData,
              srcDstType: SrcDstTypeCd.INTERDOMAIN_DESTINATION,
              tcktId: e.projectTcktInfo.dstStgDplymntTcktId,
            });
          }
        } else if (
          pageState.deploymentRequestDetail.dplymntEnv === EnvTypeCd.PRD
        ) {
          if (!!e.srcDipSelect) {
            resultIfList.push(
              {
                ...ifInfoData,
                srcDstType: SrcDstTypeCd.INTERDOMAIN_SOURCE,
                tcktId: e.projectTcktInfo.srcPrdDplymntTcktId,
              },
              {
                ...ifInfoData,
                srcDstType: SrcDstTypeCd.INTERDOMAIN_DESTINATION,
                tcktId: e.projectTcktInfo.dstPrdDplymntTcktId,
              },
            );
          }
        }
      }
      return resultIfList;
    });
    const updateifListMap: UpdateIfListMap[] = [
      {
        pjtId: pageState.deploymentRequestDetail.jediPjtId,
        ifList,
      },
    ];
    const isExistScheDtime = !!pageState.deploymentRequestDetail.scheDtime;

    const dialogState: DeploymentRequestInfomationInputDialogState = {
      ...DeploymentRequestInfomationInputDialogInitialState,
      dplymntReqType: DeploymentRequestType.INTERFACE,
      processTyp: pageState.deploymentRequestDetail.dplymntEnv,
      deploymentRequestList: [
        {
          dplymntReqSeq: pageState.deploymentRequestDetail.dplymntReqSeq,
          updateDtimeTDplymntReqMng:
            pageState.deploymentRequestDetail.updateDtimeTDplymntReqMng,
        },
      ],
      updateifListMap,
      componentMap: [],
      isShow: true,
      dplyImplType: isExistScheDtime
        ? DPLY_IMPL_TYPE.SCHEDULE_DEPLOY
        : DPLY_IMPL_TYPE.IMMEDIATELY_DEPLOY,
      dplymntDate: isExistScheDtime
        ? extractDate(pageState.deploymentRequestDetail.scheDtime)
        : DeploymentRequestInfomationInputDialogInitialState.dplymntDate,
      dplymntTime: isExistScheDtime
        ? extractHourAndMinute(pageState.deploymentRequestDetail.scheDtime)
        : DeploymentRequestInfomationInputDialogInitialState.dplymntTime,
    };
    setDeploymentRequestInfomationInputDialogState(dialogState);
  };

  return (
    <>
      {/* IF Project Infomation */}
      <>
        <FrVrfAccordion summary="Interface Project">
          <FrVrfPanel
            style={{
              "--vrf-single-line-display-field-label-width": "200px",
            }}
          >
            <FrVrfFormPanel columns={2}>
              <FrVrfSingleLineDisplayField
                label={`Interface Project Name:`}
                firstValue={pageState.deploymentRequestDetail.jediPjtName}
              />
              <FrVrfSingleLineDisplayField
                label="Interface Project Description:"
                firstValue={
                  pageState.deploymentRequestDetail.jediPjtDescription
                }
              />
            </FrVrfFormPanel>
          </FrVrfPanel>
        </FrVrfAccordion>
      </>
      {/* Main Content */}
      <br />
      <FrVrfAccordion summary="Deployment Request">
        <FrVrfPanel
          style={{
            "--vrf-single-line-display-field-label-width": "200px",
          }}
        >
          <FrVrfFormPanel columns={2}>
            <div>
              <FrVrfSingleLineDisplayField
                label="Deployment Status:"
                firstValue={
                  !!pageState.deploymentRequestDetail.dplymntResultKbnName
                    ? pageState.deploymentRequestDetail.dplymntResultKbnName
                    : pageState.deploymentRequestDetail.dplymntStsTypeName
                }
                style={{
                  "--vrf-single-line-display-field-font-size": "24px",
                  "--vrf-single-line-display-field-label-font-size": "12px",
                }}
              />
              <FrVrfFormPanel columns={2}>
                <FrVrfFormPanel>
                  <FrVrfSingleLineDisplayField
                    label="STG/PRD:"
                    firstValue={pageState.deploymentRequestDetail.dplymntEnv}
                  />
                  <FrVrfSingleLineDisplayField
                    label="Requester:"
                    firstValue={pageState.deploymentRequestDetail.reqUser}
                  />
                  {!!pageState.deploymentRequestDetail.scheDtime ? (
                    <FrVrfSingleLineDisplayField
                      label="Scheduled Date Time:"
                      firstValue={removeMilliseconds(
                        pageState.deploymentRequestDetail.scheDtime,
                      )}
                    />
                  ) : (
                    <FrVrfSingleLineDisplayField
                      label=" Scheduled Date Time (Immediately Deploy)"
                      firstValue={"Immediately Deploy"}
                    />
                  )}
                  <div>
                    {!(
                      pageMode ===
                        DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE ||
                      pageMode ===
                        DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
                    ) && (
                      <FrVrfSingleLineDisplayField
                        label="Approver:"
                        firstValue={
                          pageState.deploymentRequestDetail.dplymntAprvr
                        }
                      />
                    )}
                  </div>
                  {/* Reference, Revert Mode */}
                  <div>
                    {(pageMode ===
                      DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE ||
                      pageMode ===
                        DeploymentRequestScreenProcessingDivisionType.REVERT_MODE) && (
                      <FrVrfSingleLineDisplayField
                        label="Deployment Completed Date:"
                        firstValue={removeMilliseconds(
                          pageState.deploymentRequestDetail.dplymntCmpDtime,
                        )}
                      />
                    )}
                  </div>
                </FrVrfFormPanel>
                <FrVrfFormPanel>
                  <FrVrfSingleLineDisplayField
                    label="Request No:"
                    firstValue={
                      pageState.deploymentRequestDetail.dplymntType ===
                      DeploymentDivisionCode.REVERT
                        ? `${pageState.deploymentRequestDetail.dplymntReqSeq}(${pageState.deploymentRequestDetail.reverttargetdplymntReqSeq} Revert)`
                        : pageState.deploymentRequestDetail.dplymntReqSeq
                    }
                  />
                  <FrVrfSingleLineDisplayField
                    label="Deployment Type:"
                    firstValue={
                      pageState.deploymentRequestDetail.dplymntTypeName
                    }
                  />
                  <FrVrfSingleLineDisplayField
                    label="Environment ID:"
                    firstValue={pageState.deploymentRequestDetail.envId}
                  />
                  <FrVrfSingleLineDisplayField
                    label="Requested Date Time:"
                    firstValue={removeMilliseconds(
                      pageState.deploymentRequestDetail.reqDtime,
                    )}
                  />
                  <div>
                    {!(
                      pageMode ===
                        DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE ||
                      pageMode ===
                        DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
                    ) && (
                      <FrVrfSingleLineDisplayField
                        label="Approved Date Time:"
                        firstValue={removeMilliseconds(
                          pageState.deploymentRequestDetail.aprvrDtime,
                        )}
                      />
                    )}
                  </div>
                </FrVrfFormPanel>
              </FrVrfFormPanel>
            </div>

            <FrVrfFormPanel
              style={{
                "--vrf-single-line-display-field-label-width": "120px",
                "--vrf-multiple-line-input-field-label-width": "120px",
              }}
            >
              {/* Reference, Review, Withdraw Retry, Revert Mode */}

              <div>
                {!(
                  pageMode ===
                  DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE
                ) && (
                  <FrVrfMultipleLineInputField
                    labelContent="Review Comment:"
                    content={pageState.deploymentRequestDetail.rvwCmnt}
                    required
                    disabled={
                      pageMode !==
                      DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
                    }
                    clearButtonVisible={true}
                    onInputValueChanged={onChangeValueHandle}
                    errorMessage={errorMessages(
                      pageState.fieldPath.reviewComment,
                      isModalPage(),
                    )}
                    style={{
                      "--vrf-multiple-line-input-field-input-height": "120px",
                      "--vrf-multiple-line-input-field-input-width": "700px",
                    }}
                    constraint="[IsNotEmpty(value),Size(value, {min: 1, max: 256})]"
                  />
                )}
              </div>

              <div>
                {!(
                  pageMode ===
                    DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE ||
                  pageMode ===
                    DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
                ) && (
                  <FrVrfSingleLineDisplayField
                    label="Retry Count:"
                    firstValue={pageState.deploymentRequestDetail.retryCnt.toString()}
                  />
                )}
              </div>

              <div>
                {!(
                  pageMode ===
                    DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE ||
                  pageMode ===
                    DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
                ) && (
                  <FrVrfSingleLineDisplayField
                    label="Error Info:"
                    firstValue={pageState.deploymentRequestDetail.errorInfo}
                  />
                )}
              </div>
            </FrVrfFormPanel>
          </FrVrfFormPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <br />

      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        onInitialized={initSpreadsheetState}
        templateUrl={templateFileUrl}
        onCellEdited={onCellEdited}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />
      {/* Footer */}
      {pageState.authorized && (
        <FrVrfFooter>
          {/* before_approve_change */}
          {pageMode ===
            DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE && (
            <>
              <FrVrfButton
                content="Discard"
                theme="primary"
                size="large"
                onClick={onClickDiscardButton}
                authActId="deployment_request_detail_before_approve_change.button.discard"
              />
              <FrVrfButton
                content="Change Interrupt Type and Deployment Date"
                theme="primary"
                size="large"
                onClick={onClickChangeInterruptTypeButton}
                authActId="deployment_request_detail_before_approve_change.button.change_interrupt_type_and_deployment_date"
              />
            </>
          )}

          {/* review */}
          {pageMode ===
            DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE && (
            <>
              <FrVrfButton
                content="Reject"
                theme="primary"
                size="large"
                onClick={() => {
                  showConfirmationDialog({
                    type: "confirm",
                    title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
                    message: messageForScreen(
                      ApplicationMessageScreen.APP_SCN_00159,
                      "reject",
                    ),
                    onOkButtonClicked: () => {
                      resetUserError();
                      sendUpdateInterfaceDeploymentRequestStatusRequest({
                        processTyp:
                          DeploymentRequestProcessingDivisionType.REJECT,
                        dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
                        dplymntReqSeq:
                          pageState.deploymentRequestDetail.dplymntReqSeq,
                        tcktStsIdFrom:
                          pageState.deploymentRequestDetail.dplymntSts,
                        reviewComment:
                          pageState.deploymentRequestDetail.rvwCmnt,
                        updateDtimeTDplymntReqMng:
                          pageState.deploymentRequestDetail
                            .updateDtimeTDplymntReqMng,
                      });
                    },
                  });
                }}
                disabled={!pageState.formValidation.rvwCmnt}
                authActId="deployment_request_detail_review.button.reject"
              />
              <FrVrfButton
                content="Approve"
                theme="primary"
                size="large"
                onClick={() => {
                  showConfirmationDialog({
                    type: "confirm",
                    title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
                    message: messageForScreen(
                      ApplicationMessageScreen.APP_SCN_00159,
                      "approve",
                    ),
                    onOkButtonClicked: () => {
                      resetUserError();
                      sendUpdateInterfaceDeploymentRequestStatusRequest({
                        processTyp:
                          DeploymentRequestProcessingDivisionType.APPROVE,
                        dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
                        dplymntReqSeq:
                          pageState.deploymentRequestDetail.dplymntReqSeq,
                        tcktStsIdFrom:
                          pageState.deploymentRequestDetail.dplymntSts,
                        reviewComment:
                          pageState.deploymentRequestDetail.rvwCmnt,
                        updateDtimeTDplymntReqMng:
                          pageState.deploymentRequestDetail
                            .updateDtimeTDplymntReqMng,
                      });
                    },
                  });
                }}
                disabled={!pageState.formValidation.rvwCmnt}
                authActId="deployment_request_detail_review.button.approve"
              />
            </>
          )}
          {/* withdraw_retry */}
          {(pageMode ===
            DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE ||
            pageMode ===
              DeploymentRequestScreenProcessingDivisionType.RETRY_MODE) && (
            <>
              <FrVrfButton
                content="Discard"
                theme="primary"
                size="large"
                onClick={onClickDiscardButton}
                authActId="deployment_request_detail_withdraw_retry.button.discard"
              />
              <FrVrfButton
                content="Withdraw Request"
                theme="primary"
                size="large"
                onClick={() => {
                  showConfirmationDialog({
                    type: "confirm",
                    title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
                    message: messageForScreen(
                      ApplicationMessageScreen.APP_SCN_00159,
                      "withdraw",
                    ),
                    onOkButtonClicked: () => {
                      resetUserError();
                      sendUpdateInterfaceDeploymentRequestStatusRequest({
                        processTyp:
                          DeploymentRequestProcessingDivisionType.WITHDRAW,
                        dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
                        dplymntReqSeq:
                          pageState.deploymentRequestDetail.dplymntReqSeq,
                        tcktStsIdFrom:
                          pageState.deploymentRequestDetail.dplymntSts,
                        updateDtimeTDplymntReqMng:
                          pageState.deploymentRequestDetail
                            .updateDtimeTDplymntReqMng,
                      });
                    },
                  });
                }}
                authActId="deployment_request_detail_withdraw_retry.button.withdraw_request"
              />
              {((getDeploymentRequestResponse?.deploymentRequestDetail
                .dplymntEnv === EnvTypeCd.STG &&
                (getDeploymentRequestResponse.deploymentRequestDetail
                  .dplymntSts === TcktStsTypeIfStgDplymnt.DEPLOYMENT_FAILED ||
                  getDeploymentRequestResponse.deploymentRequestDetail
                    .dplymntSts ===
                    TcktStsTypeIfStgDplymnt.REVERT_DEPLOYMENT_FAILED)) ||
                (getDeploymentRequestResponse?.deploymentRequestDetail
                  .dplymntEnv === EnvTypeCd.PRD &&
                  (getDeploymentRequestResponse.deploymentRequestDetail
                    .dplymntSts === TcktStsTypeIfPrdDplymnt.DEPLOYMENT_FAILED ||
                    getDeploymentRequestResponse.deploymentRequestDetail
                      .dplymntSts ===
                      TcktStsTypeIfPrdDplymnt.REVERT_DEPLOYMENT_FAILED))) && (
                <FrVrfButton
                  content="Retry Request"
                  theme="primary"
                  size="large"
                  onClick={() => {
                    showConfirmationDialog({
                      type: "confirm",
                      title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
                      message: messageForScreen(
                        ApplicationMessageScreen.APP_SCN_00159,
                        "retry",
                      ),
                      onOkButtonClicked: () => {
                        resetUserError();
                        sendUpdateInterfaceDeploymentRequestStatusRequest({
                          processTyp:
                            DeploymentRequestProcessingDivisionType.RETRY,
                          dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
                          dplymntReqSeq:
                            pageState.deploymentRequestDetail.dplymntReqSeq,
                          tcktStsIdFrom:
                            pageState.deploymentRequestDetail.dplymntSts,
                          updateDtimeTDplymntReqMng:
                            pageState.deploymentRequestDetail
                              .updateDtimeTDplymntReqMng,
                        });
                      },
                    });
                  }}
                  authActId="deployment_request_detail_withdraw_retry.button.retry_request"
                />
              )}
            </>
          )}

          {/* revert */}
          {pageMode ===
            DeploymentRequestScreenProcessingDivisionType.REVERT_MODE && (
            <>
              <FrVrfButton
                content="Revert Request"
                theme="primary"
                size="large"
                onClick={() => {
                  showConfirmationDialog({
                    type: "confirm",
                    title: `Input Deployment Request Information (${pageState.deploymentRequestDetail.dplymntEnv})`,
                    message: messageForScreen(
                      ApplicationMessageScreen.APP_SCN_00159,
                      "revert",
                    ),
                    onOkButtonClicked: () => {
                      resetUserError();
                      sendUpdateInterfaceDeploymentRequestStatusRequest({
                        processTyp:
                          DeploymentRequestProcessingDivisionType.REVERT,
                        dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
                        dplymntReqSeq:
                          pageState.deploymentRequestDetail.dplymntReqSeq,
                        tcktStsIdFrom:
                          pageState.deploymentRequestDetail.dplymntSts,
                        updateDtimeTDplymntReqMng:
                          pageState.deploymentRequestDetail
                            .updateDtimeTDplymntReqMng,
                      });
                    },
                  });
                }}
                authActId="deployment_request_detail_revert.button.revert_request"
              />
            </>
          )}
        </FrVrfFooter>
      )}

      {deploymentRequestInfomationInputDialogState.isShow && (
        <DeploymentRequestInfomationInputDialogContent />
      )}
    </>
  );
};
